/**
 * Combined federal + provincial/territorial marginal tax rates, 2026.
 *
 * TRANSCRIBED, NOT DERIVED. Every figure below is copied from a published rate
 * table. Nothing here is computed, inferred, or remembered.
 *
 * Rates are public facts, so no attribution is owed and none is shown.
 *
 * SOURCE_URL is recorded for whoever replaces this table next year, and is not
 * the government: CRA publishes brackets and dividend credit rates separately,
 * never the combined marginal rates below, which take the gross-up and credit
 * arithmetic to produce. canada.ca is where the underlying rates are set, and
 * OFFICIAL_URL points readers there.
 *
 * Two properties of these rates make the arithmetic simple and are easy to get
 * wrong if you assume otherwise:
 *
 *   - Dividend rates apply to the ACTUAL dividend received, not to the
 *     grossed-up amount. The 38% gross-up and the dividend tax credit are
 *     already inside these numbers. Do not gross up again.
 *   - Capital gains rates apply to the FULL gain, not the taxable half. The 50%
 *     inclusion is already inside them. Do not halve again.
 *
 * Rates are re-indexed annually. When they change, replace the whole table and
 * bump TAX_YEAR — do not patch individual brackets, because the thresholds move
 * together and a half-updated table is worse than an old one.
 */
export const TAX_YEAR = 2026;
export const SOURCE_URL = 'https://www.taxtips.ca/marginaltaxrates.htm';
export const RETRIEVED = '2026-08-06';

/** Where the rates themselves are set, for a reader who wants the official
    figures. Not where the combined table above was transcribed from. */
export const OFFICIAL_URL =
    'https://www.canada.ca/en/revenue-agency/services/tax/individuals/frequently-asked-questions-individuals/canadian-income-tax-rates-individuals-current-previous-years.html';

/**
 * OAS recovery tax ("clawback"), for the year above.
 *
 * TRANSCRIBED, on the same terms as the rates: the threshold is re-indexed
 * annually and belongs in this file, next to the numbers that are replaced with
 * it. `threshold` is null until it is filled in — nothing computes a clawback
 * against a guessed threshold, because a wrong OAS figure is worse than a
 * missing one.
 *
 * The recovery is `rate` percent of net income above `threshold`, capped at the
 * OAS actually received. What pushes a dividend holder over is the 38% gross-up
 * on eligible dividends: net income counts the grossed-up amount, not what
 * landed in the account.
 */
export const OAS_RECOVERY: { threshold: number | null; rate: number } = { threshold: null, rate: 15 };

export interface Bracket {
    /** Upper bound of taxable income for this bracket; null is the top band. */
    upTo: number | null;
    /** Interest, foreign income, and anything else fully taxable. */
    other: number;
    /** Percent of the full gain, not of the taxable half. */
    capitalGains: number;
    /** Percent of the dividend actually received. Negative at low incomes,
        which is real: the credit can exceed the tax. */
    eligible: number;
    nonEligible: number;
}

export interface Jurisdiction {
    code: string;
    name: string;
    brackets: Bracket[];
}

const b = (upTo: number | null, other: number, capitalGains: number, eligible: number, nonEligible: number): Bracket => ({
    upTo,
    other,
    capitalGains,
    eligible,
    nonEligible,
});

export const JURISDICTIONS: Jurisdiction[] = [
    {
        code: 'AB',
        name: 'Alberta',
        brackets: [
            b(58523, 22.0, 11.0, -1.57, 12.41),
            b(61200, 28.5, 14.25, 7.4, 19.88),
            b(117045, 30.5, 15.25, 10.16, 22.18),
            b(154259, 36.0, 18.0, 17.75, 28.51),
            b(181440, 38.0, 19.0, 20.51, 30.81),
            b(185111, 41.29, 20.65, 25.05, 34.59),
            b(246813, 42.29, 21.15, 26.43, 35.74),
            b(258482, 43.29, 21.65, 27.81, 36.89),
            b(370220, 47.0, 23.5, 32.93, 41.16),
            b(null, 48.0, 24.0, 34.31, 42.31),
        ],
    },
    {
        code: 'BC',
        name: 'British Columbia',
        brackets: [
            b(50363, 19.6, 9.8, -10.24, 9.91),
            b(58523, 21.7, 10.85, -7.34, 12.32),
            b(100728, 28.2, 14.1, 1.63, 19.8),
            b(115648, 31.0, 15.5, 5.49, 23.02),
            b(117045, 32.79, 16.4, 7.96, 25.07),
            b(140430, 38.29, 19.15, 15.55, 31.4),
            b(181440, 40.7, 20.35, 18.88, 34.17),
            b(190405, 43.99, 22.0, 23.42, 37.95),
            b(258482, 46.09, 23.05, 26.32, 40.37),
            b(265545, 49.8, 24.9, 31.44, 44.64),
            b(null, 53.5, 26.75, 36.54, 48.89),
        ],
    },
    {
        code: 'MB',
        name: 'Manitoba',
        brackets: [
            b(47000, 24.8, 12.4, 2.46, 17.23),
            b(58523, 26.75, 13.38, 5.15, 19.48),
            b(100000, 33.25, 16.63, 14.12, 26.95),
            b(117045, 37.9, 18.95, 20.53, 32.3),
            b(181440, 43.4, 21.7, 28.12, 38.62),
            b(200000, 46.69, 23.35, 32.67, 42.4),
            b(258482, 47.54, 23.77, 33.84, 43.38),
            // Published as falling above $400,000, which looks like a typo and
            // is not: a provincial credit finishes phasing out, so the marginal
            // rate steps back down. Transcribed as published.
            b(400000, 51.25, 25.63, 38.96, 47.65),
            b(null, 50.4, 25.2, 37.78, 46.67),
        ],
    },
    {
        code: 'NB',
        name: 'New Brunswick',
        brackets: [
            b(52333, 23.4, 11.7, -7.76, 13.36),
            b(58523, 28.0, 14.0, -1.41, 18.65),
            b(104666, 34.5, 17.25, 7.56, 26.13),
            b(117045, 36.5, 18.25, 10.32, 28.43),
            b(181440, 42.0, 21.0, 17.91, 34.75),
            b(193861, 45.29, 22.65, 22.45, 38.53),
            b(258482, 48.79, 24.4, 27.28, 42.56),
            b(null, 52.5, 26.25, 32.4, 46.83),
        ],
    },
    {
        code: 'NL',
        name: 'Newfoundland and Labrador',
        brackets: [
            b(44678, 22.7, 11.35, 1.9, 12.04),
            b(58523, 28.5, 14.25, 9.91, 18.71),
            b(89354, 35.0, 17.5, 18.88, 26.19),
            b(117045, 36.3, 18.15, 20.67, 27.68),
            b(159528, 41.8, 20.9, 28.26, 34.01),
            b(181440, 43.8, 21.9, 31.02, 36.31),
            b(223340, 47.09, 23.55, 35.56, 40.09),
            b(258482, 49.09, 24.55, 38.32, 42.39),
            b(285319, 52.8, 26.4, 43.44, 46.66),
            b(570638, 53.8, 26.9, 44.82, 47.81),
            b(1141275, 54.3, 27.15, 45.51, 48.38),
            b(null, 54.8, 27.4, 46.2, 48.96),
        ],
    },
    {
        code: 'NS',
        name: 'Nova Scotia',
        brackets: [
            b(30995, 22.79, 11.4, -1.49, 14.1),
            b(58523, 28.95, 14.48, 7.01, 21.18),
            b(61991, 35.45, 17.73, 15.98, 28.66),
            b(97417, 37.17, 18.59, 18.35, 30.64),
            b(117045, 38.0, 19.0, 19.5, 31.59),
            b(157124, 43.5, 21.75, 27.09, 37.92),
            b(181440, 47.0, 23.5, 31.92, 41.94),
            b(258482, 50.29, 25.15, 36.46, 45.72),
            b(null, 54.0, 27.0, 41.58, 49.99),
        ],
    },
    {
        code: 'NT',
        name: 'Northwest Territories',
        brackets: [
            b(53003, 19.9, 9.95, -9.14, 5.6),
            b(58523, 22.6, 11.3, -5.41, 8.71),
            b(106009, 29.1, 14.55, 3.56, 16.18),
            b(117045, 32.7, 16.35, 8.53, 20.32),
            b(172346, 38.2, 19.1, 16.12, 26.65),
            b(181440, 40.05, 20.03, 18.67, 28.77),
            b(258482, 43.34, 21.67, 23.21, 32.55),
            b(null, 47.05, 23.53, 28.33, 36.82),
        ],
    },
    {
        code: 'NU',
        name: 'Nunavut',
        brackets: [
            b(55801, 18.0, 9.0, -3.49, 7.31),
            b(58523, 21.0, 10.5, 0.65, 10.76),
            b(111602, 27.5, 13.75, 9.62, 18.24),
            b(117045, 29.5, 14.75, 12.38, 20.54),
            b(181439, 35.0, 17.5, 19.97, 26.86),
            b(258482, 40.79, 20.4, 27.96, 33.52),
            b(null, 44.5, 22.25, 33.08, 37.79),
        ],
    },
    {
        code: 'ON',
        name: 'Ontario',
        brackets: [
            b(53891, 19.05, 9.53, -8.24, 8.09),
            b(58523, 23.15, 11.58, -2.58, 12.8),
            b(94907, 29.65, 14.83, 6.39, 20.28),
            b(107785, 31.48, 15.74, 8.92, 22.38),
            b(111814, 33.89, 16.95, 12.24, 25.16),
            b(117045, 37.91, 18.95, 17.79, 29.78),
            b(150000, 43.41, 21.7, 25.38, 36.1),
            b(181440, 44.97, 22.48, 27.53, 37.9),
            b(220000, 48.26, 24.13, 32.07, 41.68),
            b(258482, 49.82, 24.91, 34.22, 43.47),
            b(null, 53.53, 26.76, 39.34, 47.74),
        ],
    },
    {
        code: 'PE',
        name: 'Prince Edward Island',
        brackets: [
            b(33928, 23.5, 11.75, -2.79, 15.15),
            b(58523, 27.47, 13.74, 2.69, 19.71),
            b(65820, 33.97, 16.99, 11.66, 27.19),
            b(106890, 37.1, 18.55, 15.98, 30.79),
            b(117045, 38.12, 19.06, 17.39, 31.96),
            b(142250, 43.62, 21.81, 24.98, 38.28),
            b(181440, 45.0, 22.5, 26.88, 39.87),
            b(200000, 48.29, 24.15, 31.42, 43.65),
            b(258482, 49.29, 24.65, 32.8, 44.8),
            b(null, 53.0, 26.5, 37.92, 49.07),
        ],
    },
    {
        code: 'QC',
        name: 'Quebec',
        brackets: [
            b(54345, 25.69, 12.85, 2.0, 16.94),
            b(58523, 30.69, 15.35, 8.9, 22.69),
            b(108680, 36.12, 18.06, 16.39, 28.93),
            b(117045, 41.12, 20.56, 23.29, 34.68),
            b(132245, 45.71, 22.86, 29.63, 39.96),
            b(181440, 47.46, 23.73, 32.04, 41.97),
            b(258482, 50.21, 25.1, 35.83, 45.13),
            b(null, 53.31, 26.65, 40.11, 48.7),
        ],
    },
    {
        code: 'SK',
        name: 'Saskatchewan',
        brackets: [
            b(54532, 24.5, 12.25, -2.1, 14.89),
            b(58523, 26.5, 13.25, 0.66, 17.19),
            b(117045, 33.0, 16.5, 9.63, 24.67),
            b(155805, 38.5, 19.25, 17.22, 30.99),
            b(181440, 40.5, 20.25, 19.98, 33.29),
            b(258482, 43.79, 21.9, 24.52, 37.07),
            b(null, 47.5, 23.75, 29.64, 41.34),
        ],
    },
    {
        code: 'YT',
        name: 'Yukon',
        brackets: [
            b(58523, 20.4, 10.2, -9.16, 12.3),
            b(117045, 29.5, 14.75, 3.4, 22.77),
            b(181440, 36.9, 18.45, 13.61, 31.28),
            b(258482, 42.22, 21.11, 20.95, 37.39),
            b(500000, 45.8, 22.9, 25.89, 41.51),
            b(null, 48.0, 24.0, 28.93, 44.04),
        ],
    },
];

/** The bracket a given taxable income falls in. */
export const bracketFor = (code: string, income: number): Bracket => {
    const jurisdiction = JURISDICTIONS.find((j) => j.code === code) ?? JURISDICTIONS.find((j) => j.code === 'ON')!;
    return jurisdiction.brackets.find((band) => band.upTo == null || income <= band.upTo) ?? jurisdiction.brackets[jurisdiction.brackets.length - 1];
};

export const jurisdiction = (code: string): Jurisdiction | undefined => JURISDICTIONS.find((j) => j.code === code);
